import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FiAlertCircle, FiCheck } from "react-icons/fi";
import Button from "../../../../../components/shared/Button/Button";
import ModalMobile from "../../../../../components/shared/Modal/ModalMobile";
import { useUserData } from "../../../../../hooks/useUserData";
import Spinner from "../../../../../components/shared/spinner/Spinner";
import { downloadFile } from "../../../utils/download";

const TEXT_MAP = {
  hasNotAnalyzed: {
    cta: "Try Again",
    description: "It looks like you selected not analyzed video(s).",
    title: "Not Analyzed Videos",
    additionalText: () =>
      "Please select videos that are analyzed to generate an accurate report.",
  },
  missingCredits: {
    cta: "Get Credits",
    description:
      "It looks like you don't have any credits available to generate a Swing Score Report.",
    title: "Oops!",
    additionalText: (credits) => `You have ${credits} credits available.`,
  },
  mixedCameraAngles: {
    cta: "Try Again",
    description:
      "It looks like you selected videos that are recorded from Down The Line:",
    title: "Down the Line videos",
    additionalText: () =>
      "Please select videos that are from Face On angle to generate an accurate report.",
  },
  mixedClubs: {
    cta: "Try Again",
    description: "It looks like you selected videos with different club types:",
    title: "Incompatible Mixed Clubs",
    additionalText: () =>
      "Please select videos that are Driver only, or 5/6/7 Irons.",
  },
  mixedUsers: {
    cta: "Try Again",
    description: "It looks like you selected videos with different golfers:",
    title: "Mixed Golfers",
    additionalText: () =>
      "Please select videos that are from the same golfer to generate an accurate report.",
  },
};

async function handleDownloadFromUrl(url, filename) {
  const res = await fetch(url);
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  await downloadFile(blob, filename);
}

export const ReportGenerationDialog = ({
  onClose,
  onCreditSpent,
  onPrepare,
  open,
  verificationState,
  hasFetchingFailed,
}) => {
  const { userData } = useUserData();

  const credits = userData?.reportCredits || 0;

  const [dialogState, setDialogState] = useState("confirmation");
  const [loading, setLoading] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [reportURL, setReportURL] = useState("");

  // prepare / handle errors
  useEffect(() => {
    if (!open) return;
    if (verificationState.isError) {
      setDialogState("error");
      return;
    }
    setDialogState("confirmation");
    (async () => {
      setPreparing(true);
      await onPrepare();
      setPreparing(false);
    })();
  }, [open, verificationState]);

  useEffect(() => {
    if (hasFetchingFailed) {
      setDialogState("fetchingFailed");
    }
  }, [hasFetchingFailed]);

  const handleClose = () => {
    onClose();
    if (dialogState === "success") window.location.reload();
    setDialogState("confirmation");
  };

  const handleGenerate = async () => {
    try {
      setLoading(true);
      const url = await onCreditSpent();
      if (!url) throw new Error("No URL returned");
      setReportURL(url);
      setDialogState("success");
    } catch (error) {
      console.error(error?.message);
      toast.error(error?.message || "Failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalMobile
      className="lg:max-w-lg p-6"
      isOpen={open}
      onClose={handleClose}
      showClose={true}
    >
      {/* ——— Loading state ——— */}
      {preparing ? (
        <div className="flex h-[200px] items-center justify-center">
          <Spinner color="primary500" size="md" />
        </div>
      ) : (
        <>
          {/* ——— CONFIRMATION ——— */}
          {dialogState === "confirmation" && (
            <>
              <div className="mb-4 flex items-center gap-2">
                <div className="flex size-10 items-center justify-center rounded-full bg-primary50">
                  <FiAlertCircle className="text-primary700 size-5" />
                </div>
                <h2 className="text-lg font-semibold">
                  Use 1 Swing Score Credit
                </h2>
              </div>

              <p className="mb-2 text-sm text-gray600">
                Are you sure you want to generate a Swing Score Report?
              </p>
              <p className="mb-4 text-sm font-semibold text-gray800">
                You have {credits} credits available
              </p>

              <Button
                className="w-full rounded-full py-3 font-semibold"
                isDisabled={loading}
                onClick={handleGenerate}
              >
                {loading ? "Generating…" : "Use 1 Credit"}
              </Button>
            </>
          )}

          {/* ——— SUCCESS ——— */}
          {dialogState === "success" && (
            <>
              <div className="mb-4 flex items-center gap-2">
                <div className="flex size-10 items-center justify-center rounded-full bg-primary50">
                  <FiCheck className="text-primary700 size-5" />
                </div>
                <h2 className="text-lg font-semibold">
                  Swing Score Report Generated
                </h2>
              </div>

              <p className="text-sm text-gray600">
                You can access the swing score report at any time in your
                Credits.
              </p>

              <a
                className="text-primary700 mt-3 inline-flex font-semibold hover:underline"
                href="/credits"
              >
                Credits ↗
              </a>

              <div className="mt-6 flex gap-2">
                <Button
                  className="w-full rounded-full py-3 font-semibold"
                  color="gray100"
                  onClick={() => window.open(reportURL, "_blank")}
                >
                  View
                </Button>
                <Button
                  className="w-full rounded-full py-3 font-semibold"
                  onClick={() =>
                    handleDownloadFromUrl(reportURL, "Swing Score Report.pdf")
                  }
                >
                  Download PDF
                </Button>
              </div>
            </>
          )}

          {dialogState === "fetchingFailed" && (
            <>
              <div className="mb-4 flex items-center gap-2">
                <div className="flex size-10 items-center justify-center rounded-full bg-warning50">
                  <FiAlertCircle className="size-5 text-warning700" />
                </div>
                <h2 className="text-lg font-semibold">
                  Report Generation Failed
                </h2>
              </div>

              <p className="mb-1 text-sm text-gray600">
                It looks like there was an error during generation of report.
              </p>

              <p className="mb-6 text-sm font-semibold text-gray800">
                Please try again later.
              </p>

              <Button
                className="w-full rounded-full py-3 font-semibold"
                onClick={handleClose}
              >
                Close
              </Button>
            </>
          )}

          {/* ——— ERROR ——— */}
          {dialogState === "error" && verificationState.isError && (
            <>
              <div className="mb-4 flex items-center gap-2">
                <div className="flex size-10 items-center justify-center rounded-full bg-warning50">
                  <FiAlertCircle className="size-5 text-warning700" />
                </div>
                <h2 className="text-lg font-semibold">
                  {TEXT_MAP[verificationState.errorType].title}
                </h2>
              </div>

              <p className="mb-1 text-sm text-gray600">
                {TEXT_MAP[verificationState.errorType].description}
              </p>
              {verificationState.errorValues && (
                <p className="mb-1 text-sm text-gray700">
                  {verificationState.errorValues}
                </p>
              )}
              <p className="mb-6 text-sm font-semibold text-gray800">
                {TEXT_MAP[verificationState.errorType].additionalText(credits)}
              </p>

              {verificationState.errorType === "missingCredits" ? (
                <a
                  className="bg-primary50 block w-full rounded-full px-4 py-3 text-center font-semibold text-white"
                  href="/credits"
                >
                  {TEXT_MAP[verificationState.errorType].cta}
                </a>
              ) : (
                <Button
                  className="w-full rounded-full py-3 font-semibold"
                  onClick={handleClose}
                >
                  {TEXT_MAP[verificationState.errorType].cta}
                </Button>
              )}
            </>
          )}
        </>
      )}
    </ModalMobile>
  );
};
